import { type CSSProperties } from "react";
import type { VelaSummary } from "../overviewTypes";

/**
 * Compact Vela banner at the top of the populated Accounting Overview.
 * Carries the Employee_content.svg animation on the left + aggregated
 * summary across every Accounting sub-page + Review all CTA.
 * Hides entirely when summary.totalCount === 0 (nothing dead).
 *
 * Responsive:
 *   desktop (>960px): inline-flex row, animation left, text middle, button right
 *   tablet (641-960px): same row layout, animation shrinks to 92x76
 *   phone (<=640px): stacks vertically, animation on top, text centered, button full width
 */

interface VelaBannerProps {
  summary: VelaSummary;
  onReviewAll: () => void;
  illustrationSrc: string;
}

const PALETTE = {
  ink2: "#263238",
  vela: "#A855F7",
  velaDeep: "#4C1D95",
  velaFade: "#F3EFFB",
};

export function VelaBanner({ summary, onReviewAll, illustrationSrc }: VelaBannerProps) {
  if (summary.totalCount === 0) return null;

  const parts: string[] = [];
  if (summary.breakdown.toReview > 0)         parts.push(`${summary.breakdown.toReview} transactions to review`);
  if (summary.breakdown.missingReceipts > 0)  parts.push(`${summary.breakdown.missingReceipts} missing receipts`);
  if (summary.breakdown.overdueReconcile > 0) parts.push(`${summary.breakdown.overdueReconcile} account${summary.breakdown.overdueReconcile === 1 ? "" : "s"} to reconcile`);
  if (summary.breakdown.pendingAccountant > 0) parts.push(`${summary.breakdown.pendingAccountant} accountant invite pending`);

  return (
    <div className="novala-vela-banner" style={bannerStyle}>
      <div className="novala-vela-anim vela-float" style={animWrapStyle}>
        <img src={illustrationSrc} alt="" style={animImgStyle} />
      </div>
      <div className="novala-vela-body" style={bodyStyle}>
        <div className="novala-vela-head" style={headRowStyle}>
          <span style={dotStyle}>V</span>
          <span style={headLabelStyle}>
            Vela has {summary.totalCount} thing{summary.totalCount === 1 ? "" : "s"} across Accounting
          </span>
        </div>
        <p style={bodyTextStyle}>{parts.join(", ")}.</p>
      </div>
      <button type="button" className="novala-vela-btn" style={btnStyle} onClick={onReviewAll}>
        Review all
      </button>
      <style>{`
        @keyframes velaFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        .vela-float { animation: velaFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .vela-float { animation: none; }
        }
        @media (max-width: 640px) {
          .novala-vela-banner {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            text-align: center !important;
            min-height: unset !important;
            padding: 16px 14px !important;
          }
          .novala-vela-body { align-items: center !important; }
          .novala-vela-head { justify-content: center !important; }
          .novala-vela-btn { width: 100%; max-width: 240px; }
        }
        @media (min-width: 641px) and (max-width: 960px) {
          .novala-vela-anim { width: 92px !important; height: 76px !important; }
          .novala-vela-banner { max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

const bannerStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 16,
  background: PALETTE.velaFade,
  borderRadius: 12,
  padding: "14px 20px 14px 16px",
  marginBottom: 32,
  maxWidth: 860,
  minHeight: 180,
};

const animWrapStyle: CSSProperties = {
  width: 180, height: 150, flexShrink: 0,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
};

const animImgStyle: CSSProperties = {
  width: "100%", height: "100%", objectFit: "contain", display: "block",
};

const bodyStyle: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 0,
};

const headRowStyle: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 8,
};

const dotStyle: CSSProperties = {
  width: 22, height: 22, borderRadius: "50%",
  background: PALETTE.vela, color: "#fff",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontSize: 10, fontWeight: 800, flexShrink: 0,
};

const headLabelStyle: CSSProperties = {
  fontSize: 13.5, fontWeight: 700, color: PALETTE.velaDeep,
};

const bodyTextStyle: CSSProperties = {
  fontSize: 12.5, color: PALETTE.ink2, lineHeight: 1.5, margin: 0,
};

const btnStyle: CSSProperties = {
  background: PALETTE.velaDeep, color: "#fff", border: 0,
  padding: "8px 16px", borderRadius: 7,
  fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  fontFamily: "inherit", flexShrink: 0, alignSelf: "center",
};